import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  getSystemProblems,
  queryKeys,
  SYSTEM_PROBLEMS_PAGE_SIZE,
  SystemProblem,
  SystemProblemCategory,
  SystemProblemDashboardResponse,
  SystemProblemSeverity,
  SystemProblemStatus,
} from '@vgrubs/components';

type Filters = {
  status: SystemProblemStatus | null;
  locationId: number | null;
  severity: SystemProblemSeverity | null;
  category: SystemProblemCategory | null;
};

/**
 * A page of problems at a time, newest first as the server sends them.
 *
 * `useSystemProblemsSocket` puts a new problem on top of the first page, which
 * pushes every later row one place down on the server - so the next page can
 * start with a row already shown. Repeats are dropped by id here rather than
 * by refetching everything on every event.
 */
export const useSystemProblemsList = ({
  status,
  locationId,
  severity,
  category,
}: Filters) => {
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: queryKeys.systemProblems.list(
      status,
      locationId,
      severity,
      category
    ),
    queryFn: async ({ pageParam }) => {
      const { data } = await getSystemProblems({
        page: pageParam,
        limit: SYSTEM_PROBLEMS_PAGE_SIZE,
        ...(status ? { status } : {}),
        ...(locationId ? { locationId } : {}),
        ...(severity ? { severity } : {}),
        ...(category ? { category } : {}),
      });

      /* Every page carries the unfiltered open count, so looking at the list
         corrects the Dashboard card's number for free. */
      queryClient.setQueriesData<SystemProblemDashboardResponse>(
        { queryKey: queryKeys.systemProblems.dashboard },
        (card) => card && { ...card, open: data.data.open }
      );

      return data.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      pages.length * SYSTEM_PROBLEMS_PAGE_SIZE < lastPage.total
        ? pages.length + 1
        : undefined,
  });

  const seen = new Set<number>();
  const problems: SystemProblem[] = [];
  (query.data?.pages ?? []).forEach((page) =>
    page.items.forEach((row) => {
      if (seen.has(row.id)) return;
      seen.add(row.id);
      problems.push(row);
    })
  );

  return {
    problems,
    /** Every open problem, whatever the filters - for the Open option. */
    open: query.data?.pages[0]?.open ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
  };
};
